import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useDashboard } from "context/DashboardContext";
import { useAuth } from "context/AuthContext";
import {
  Badge,
  DataTable,
  LoadState,
  Page,
  Panel,
  Stats,
  dateTime,
  money,
  useFinanceData,
} from "layouts/finance/shared";
import { useFinance } from "context/FinanceContext";

export default function Dashboard() {
  const {
    dashboardData,
    loading,
    dashboardError,
    transactions,
    transactionsError,
    loadingTransactions,
    refreshData,
  } = useDashboard();
  const { hasAccess } = useAuth();
  const { queue, error } = useFinance();
  const walletSummary = useFinanceData(
    "/sub-users/summary",
    undefined,
    { enabled: hasAccess("user-management") }
  );
  const recent = useMemo(
    () =>
      [...transactions]
        .sort(
          (a, b) => new Date(b.transactionDate) - new Date(a.transactionDate)
        )
        .slice(0, 8),
    [transactions]
  );
  const stats = dashboardData;
  return (
    <Page
      eyebrow="Admin workspace"
      title="Operations overview"
      description="Your merchant platform at a glance."
      actions={
        <button className="fin-button secondary" onClick={refreshData}>
          Refresh overview
        </button>
      }
    >
      <LoadState
        loading={loading && !stats}
        error={dashboardError}
        refresh={refreshData}
      />
      {stats && !dashboardError && (
        <Stats
          items={[
            ["Available balance", money(stats.balance)],
            ["Successful transactions", stats.successfulCount ?? "—"],
            ["Failed transactions", stats.failedCount ?? "—"],
            ["Pending settlements", stats.pendingSettlement ?? "—"],
          ]}
        />
      )}
      {hasAccess("user-management") && (
        <Panel
          title="Platform float & sub-user compliance"
          subtitle="Aggregate sub-user totals and wallet float held across all merchants."
        >
          <LoadState {...walletSummary} />
          {walletSummary.data && (
            <>
              <Stats
                items={[
                  ["Total sub-users", walletSummary.data.totalSubUsers ?? "—"],
                  ["Active", walletSummary.data.activeSubUsers ?? "—"],
                  ["Suspended", walletSummary.data.suspendedSubUsers ?? "—"],
                  ["Terminated", walletSummary.data.terminatedSubUsers ?? "—"],
                  [
                    "Total wallet float",
                    money(walletSummary.data.totalFloatBalance),
                  ],
                ]}
              />
              <div className="toolbar" style={{ marginTop: 16 }}>
                <Link className="table-link" to="/user-management">
                  Manage sub-users →
                </Link>
                <Link className="table-link" to="/wallets">
                  Open wallet directory →
                </Link>
                <button
                  className="fin-button secondary"
                  onClick={walletSummary.refresh}
                >
                  Refresh metrics
                </button>
              </div>
            </>
          )}
        </Panel>
      )}
      <div className="finance-grid">
        <Panel title="Workspace">
          <div className="quick-links">
            {hasAccess("merchants") && (
              <Link to="/merchants">
                <strong>Merchant management →</strong>
                <span>Review merchant profiles and verification documents</span>
              </Link>
            )}
            {hasAccess("transactions") && (
              <Link to="/transactions">
                <strong>Transaction activity →</strong>
                <span>Search, filter, and export payment records</span>
              </Link>
            )}
            {hasAccess("ledger") && (
              <Link to="/ledger">
                <strong>Ledger balances →</strong>
                <span>Inspect merchant credits, debits, and balances</span>
              </Link>
            )}
          </div>
        </Panel>
        {hasAccess("finance") && (
          <Panel title="Finance workspace">
            <div className="quick-links">
              <Link to="/payout-approvals">
                <strong>
                  {error
                    ? "Payout approvals"
                    : `${queue.length} payouts awaiting review`}{" "}
                  →
                </strong>
                <span>Review reserved disbursements through dual custody</span>
              </Link>
              <Link to="/finance-overview">
                <strong>Platform liquidity →</strong>
                <span>
                  Merchant liabilities, receivables, fee revenue, and ledger
                  health
                </span>
              </Link>
              <Link to="/settlements">
                <strong>T+1 settlement batches →</strong>
                <span>Review and authorize pending settlement releases</span>
              </Link>
            </div>
          </Panel>
        )}
      </div>
      {hasAccess("transactions") && (
        <Panel
          title="Recent transactions"
          subtitle="Latest records from the available transaction response."
        >
          <LoadState
            loading={loadingTransactions && !recent.length}
            error={transactionsError}
            refresh={refreshData}
          />
          {!transactionsError && (
            <DataTable
              rowKey="id"
              rows={recent}
              columns={[
                { key: "merchantName", label: "Merchant" },
                {
                  key: "amount",
                  label: "Amount",
                  render: (row) => money(row.amount),
                },
                { key: "paysureRef", label: "Reference" },
                { key: "channel", label: "Channel" },
                {
                  key: "transactionDate",
                  label: "Date",
                  render: (row) => dateTime(row.transactionDate),
                },
                {
                  key: "status",
                  label: "Status",
                  render: (row) => <Badge value={row.status} />,
                },
              ]}
            />
          )}
        </Panel>
      )}
    </Page>
  );
}
